import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tag, Plus, Trash2, Loader2, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCoupons() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/coupons")
            .then((r) => setItems(r.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const remove = async (id, code) => {
        if (!window.confirm(`Apagar cupão ${code}?`)) return;
        await api.delete(`/admin/coupons/${id}`);
        toast.success("Cupão apagado");
        load();
    };

    return (
        <div data-testid="admin-coupons">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="label-mono text-magenta">Cupões</div>
                    <h1 className="font-display font-black text-3xl">Códigos promocionais</h1>
                </div>
                <button
                    onClick={() => setEditing({ code: "", discount_type: "percent", discount_value: 10, min_order: 0, max_uses_per_user: 1, active: true })}
                    className="btn-primary"
                    data-testid="new-coupon-btn"
                >
                    <Plus size={16} /> Novo cupão
                </button>
            </div>

            {loading ? <Loader2 className="animate-spin" /> : items.length === 0 ? (
                <div className="text-center py-16 border-2 border-dashed border-ink/30">
                    <Tag size={32} className="mx-auto mb-3 text-ink/30" />
                    <p className="font-mono text-ink/60">Nenhum cupão criado.</p>
                </div>
            ) : (
                <div className="border-2 border-ink bg-white">
                    <table className="w-full font-mono text-sm">
                        <thead className="bg-ink text-white">
                            <tr><th className="p-3 text-left">Código</th><th>Desconto</th><th>Min. encomenda</th><th>Limite</th><th>Usos</th><th>Activo</th><th></th></tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} className="border-b border-ink/10" data-testid={`coupon-row-${c.code}`}>
                                    <td className="p-3 font-bold">{c.code}</td>
                                    <td className="text-center">{c.discount_type === "percent" ? `${c.discount_value}%` : `${c.discount_value}€`}</td>
                                    <td className="text-center">{c.min_order > 0 ? `${c.min_order}€` : "—"}</td>
                                    <td className="text-center">{c.max_uses ?? "∞"}</td>
                                    <td className="text-center">{c.used_count}/{c.max_uses ?? "∞"}</td>
                                    <td className="text-center">
                                        <span className={`px-2 py-0.5 text-xs ${c.active ? "bg-accent-green text-white" : "bg-ink/20"}`}>
                                            {c.active ? "ON" : "OFF"}
                                        </span>
                                    </td>
                                    <td className="text-right p-3">
                                        <button onClick={() => remove(c.id, c.code)} className="text-red-600 hover:underline" data-testid={`delete-coupon-${c.code}`}>
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {editing && <CouponModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
        </div>
    );
}

function CouponModal({ initial, onClose, onSaved }) {
    const [data, setData] = useState(initial);
    const [busy, setBusy] = useState(false);

    const upd = (k, v) => setData({ ...data, [k]: v });

    const save = async () => {
        if (!data.code) { toast.error("Código obrigatório"); return; }
        setBusy(true);
        try {
            await api.post("/admin/coupons", data);
            toast.success("Cupão criado");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-ink/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white border-2 border-ink max-w-lg w-full">
                <div className="flex items-center justify-between p-4 border-b-2 border-ink">
                    <h2 className="font-display font-black text-xl">Novo cupão</h2>
                    <button onClick={onClose}><X size={18} /></button>
                </div>
                <div className="p-6 space-y-4">
                    <label className="block">
                        <span className="label-mono mb-1 block">Código</span>
                        <input value={data.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} className="input-edge" placeholder="BLACKFRIDAY20" data-testid="coupon-code-input" />
                    </label>
                    <label className="block">
                        <span className="label-mono mb-1 block">Descrição (opcional)</span>
                        <input value={data.description || ""} onChange={(e) => upd("description", e.target.value)} className="input-edge" placeholder="Black Friday 2026" />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="label-mono mb-1 block">Tipo</span>
                            <select value={data.discount_type} onChange={(e) => upd("discount_type", e.target.value)} className="input-edge">
                                <option value="percent">Percentagem (%)</option>
                                <option value="fixed">Fixo (€)</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="label-mono mb-1 block">Valor</span>
                            <input type="number" value={data.discount_value} onChange={(e) => upd("discount_value", parseFloat(e.target.value) || 0)} className="input-edge" data-testid="coupon-value-input" />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="label-mono mb-1 block">Mín. encomenda (s/IVA)</span>
                            <input type="number" value={data.min_order} onChange={(e) => upd("min_order", parseFloat(e.target.value) || 0)} className="input-edge" />
                        </label>
                        <label className="block">
                            <span className="label-mono mb-1 block">Máx. usos por cliente</span>
                            <input type="number" value={data.max_uses_per_user || 1} onChange={(e) => upd("max_uses_per_user", parseInt(e.target.value) || 1)} className="input-edge" />
                        </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="label-mono mb-1 block">Total máximo de usos (opcional)</span>
                            <input type="number" value={data.max_uses || ""} onChange={(e) => upd("max_uses", e.target.value ? parseInt(e.target.value) : null)} className="input-edge" placeholder="ilimitado" />
                        </label>
                        <label className="block">
                            <span className="label-mono mb-1 block">Válido até (opcional)</span>
                            <input type="datetime-local" value={data.valid_until ? data.valid_until.slice(0, 16) : ""} onChange={(e) => upd("valid_until", e.target.value ? new Date(e.target.value).toISOString() : null)} className="input-edge" />
                        </label>
                    </div>
                    <label className="flex items-center gap-2 font-mono text-sm">
                        <input type="checkbox" checked={data.active} onChange={(e) => upd("active", e.target.checked)} />
                        Cupão activo
                    </label>
                </div>
                <div className="p-4 border-t-2 border-ink flex gap-3">
                    <button onClick={save} disabled={busy} className="btn-primary flex-1" data-testid="save-coupon-btn">
                        {busy && <Loader2 className="animate-spin" size={16} />}
                        Guardar
                    </button>
                    <button onClick={onClose} className="btn-secondary">Cancelar</button>
                </div>
            </div>
        </div>
    );
}
